import { api } from '../api/endpoints'
import type { PresignRequest } from '../api/types'
import { isImageType } from '../lib/files'
import { prepareImage } from '../lib/image/downscale'

export interface UploadResult {
  attachmentId: string
  fileName: string
  contentType: string
  sizeBytes: number
  width: number | null
  height: number | null
}

/** Headers XMLHttpRequest refuses to set; the browser adds them itself. */
const FORBIDDEN = new Set(['content-length', 'host', 'connection', 'accept-encoding', 'origin', 'referer'])

/** PUT the bytes with exactly the presigned headers, reporting progress. */
export function putWithProgress(
  url: string,
  headers: Record<string, string>,
  body: Blob,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload cancelled', 'AbortError'))
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    let hasContentType = false
    for (const [name, value] of Object.entries(headers)) {
      if (FORBIDDEN.has(name.toLowerCase())) continue
      if (name.toLowerCase() === 'content-type') hasContentType = true
      xhr.setRequestHeader(name, value)
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1)
        resolve()
      } else reject(new Error(`Upload failed (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection.'))
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    // Without a Content-Type in the signed headers, send an untyped blob so the browser adds none.
    xhr.send(hasContentType ? body : body.slice(0, body.size, ''))
  })
}

export interface PreparedUpload {
  file: File
  contentType: string
  width: number | null
  height: number | null
}

/** Downscale large images (not GIFs) before upload. */
export async function prepareUpload(file: File, contentType: string): Promise<PreparedUpload> {
  if (!isImageType(contentType)) return { file, contentType, width: null, height: null }
  const prepared = await prepareImage(file, contentType)
  return { file: prepared.file, contentType: prepared.file.type || contentType, width: prepared.width, height: prepared.height }
}

/** What the server keeps with a video or audio upload: its length when known, and a voice message's waveform. */
export type ClipDetails = Pick<PresignRequest, 'voice' | 'durationMs' | 'waveform'>

/** Presign → PUT to object storage. The caller sends the message with the attachment id. */
export async function uploadAttachment(
  channelId: string,
  prepared: PreparedUpload,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  details: ClipDetails = {},
): Promise<UploadResult> {
  const { file, contentType } = prepared
  const body: PresignRequest = { fileName: file.name, contentType, sizeBytes: file.size }
  if (details.voice) body.voice = true
  if (details.durationMs) body.durationMs = Math.round(details.durationMs)
  if (details.waveform?.length) body.waveform = details.waveform
  const presign = await api.presign(channelId, body, signal)
  await putWithProgress(presign.uploadUrl, presign.headers, file, onProgress, signal)
  return {
    attachmentId: presign.attachmentId,
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
    width: prepared.width,
    height: prepared.height,
  }
}
